import { Listbox, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { FieldValues, Path, Control, useController, PathValue } from "react-hook-form";
import ChevronRight from "@/svgs/chevron-right.svg";
import { GQLSelectOption } from "graphql/generated-types";

type SelectProps<T extends FieldValues> = {
	label: string;
	formKey: Path<T>;
	options: GQLSelectOption[];
	control: Control<T>;
};

const Select = <T extends FieldValues>({ label, formKey, options, control }: SelectProps<T>) => {
	const {
		field: { ref, value, onChange },
		fieldState: { error },
	} = useController({
		name: formKey,
		control,
		defaultValue: "" as PathValue<T, Path<T>>,
		rules: {
			required: { value: true, message: "Please select an option" },
		},
	});

	return (
		<div className="to-lg:text-14px relative w-full whitespace-nowrap pb-1">
			<label className="mb-1 block whitespace-pre-wrap">{label}</label>
			<Listbox value={value} onChange={onChange}>
				<div className="relative">
					<Listbox.Button className="border-1 border-grey sm:text-14px relative w-full cursor-default rounded-lg bg-transparent py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300">
						<span className="truncate">{options.find((option) => option.value === value)?.label || "Select"}</span>
						<span className="pointer-events-none absolute inset-y-0 right-2 flex items-center pr-2">
							<ChevronRight className="stroke-purple w-2 rotate-90" aria-hidden="true" />
						</span>
					</Listbox.Button>
					<Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
						<Listbox.Options className="bg-charcoal ring-grey sm:text-14px absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg py-1 text-base shadow-lg ring-1 focus:outline-none">
							{options.map((option, i) => (
								<Listbox.Option
									key={i}
									className={({ active }) =>
										`relative cursor-default select-none py-2 pl-3 pr-4 ${active ? "bg-purple" : ""}`
									}
									value={option.value}
								>
									{({ selected }) => (
										<>
											<span ref={ref} className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
												{option.label}
											</span>
										</>
									)}
								</Listbox.Option>
							))}
						</Listbox.Options>
					</Transition>
				</div>
			</Listbox>
			{error && <p className="text-purple whitespace-pre-wrap">{error.message as string}</p>}
		</div>
	);
};

export default Select;
